import { useState } from 'react';

export interface MessageTemplateItem {
  id: string;
  name: string;
  subject: string | null;
  bodyHtml: string;
  bodyText: string | null;
  storeId: string | null;
  store?: { id: string; name: string } | null;
  categoryId?: string | null;
  category?: { id: string; name: string; position: number } | null;
  shortcut?: string | null;
  autoSend?: boolean;
  position?: number;
}

export function filterMessageTemplates(templates: MessageTemplateItem[], query: string) {
  const q = query.toLocaleLowerCase('pl');
  return templates.filter(t => !q || t.name.toLocaleLowerCase('pl').includes(q) || t.shortcut?.toLocaleLowerCase('pl').includes(q))
    .sort((a, b) => (a.category?.position ?? Number.MAX_SAFE_INTEGER) - (b.category?.position ?? Number.MAX_SAFE_INTEGER)
      || (a.category?.name || 'Bez kategorii').localeCompare(b.category?.name || 'Bez kategorii', 'pl')
      || (a.position ?? 0) - (b.position ?? 0) || a.name.localeCompare(b.name, 'pl')).slice(0, 20);
}

export function MessageTemplateChoices({ templates, onSelect, disabled = false, query, activeIndex }: {
  templates: MessageTemplateItem[];
  onSelect: (template: MessageTemplateItem) => void;
  disabled?: boolean;
  query?: string;
  activeIndex?: number;
}) {
  const [categoryId, setTemplatePickerCategoryId] = useState('all');
  const categories = new Map(templates.flatMap(t => t.category ? [[t.category.id, t.category] as const] : []));
  const templatePickerCategories = [...categories.values()].sort((a, b) => a.position - b.position || a.name.localeCompare(b.name, 'pl'));
  const templatePickerCategoryId = categoryId === 'uncategorized' || categories.has(categoryId) ? categoryId : 'all';
  const visiblePickerTemplates = query !== undefined ? filterMessageTemplates(templates, query)
    : templates.filter(t => templatePickerCategoryId === 'all' || (templatePickerCategoryId === 'uncategorized' ? !t.categoryId : t.categoryId === templatePickerCategoryId));
  return (
    <>
                <div className="space-y-2 border-b border-border-subtle px-3 py-2">
                  <p className="text-xs font-medium text-text-secondary">Szablony wiadomości</p>
                  {query === undefined && (templatePickerCategories.length > 0 || templates.some(template => !template.categoryId)) && (
                    <div className="flex max-w-full gap-1 overflow-x-auto pb-0.5" aria-label="Kategorie szablonów">
                      <button
                        type="button"
                        onClick={() => setTemplatePickerCategoryId('all')}
                        className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium transition-colors ${
                          templatePickerCategoryId === 'all'
                            ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                            : 'border-border text-text-muted hover:text-text-primary'
                        }`}
                      >
                        Wszystkie
                      </button>
                      {templatePickerCategories.map(category => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => setTemplatePickerCategoryId(category.id)}
                          className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium transition-colors ${
                            templatePickerCategoryId === category.id
                              ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                              : 'border-border text-text-muted hover:text-text-primary'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                      {templates.some(template => !template.categoryId) && (
                        <button
                          type="button"
                          onClick={() => setTemplatePickerCategoryId('uncategorized')}
                          className={`shrink-0 rounded-full border px-2 py-1 text-[10px] font-medium transition-colors ${
                            templatePickerCategoryId === 'uncategorized'
                              ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                              : 'border-border text-text-muted hover:text-text-primary'
                          }`}
                        >
                          Bez kategorii
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="max-h-60 overflow-y-auto">
                  {templates.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-text-muted">
                      Brak szablonów. Dodaj je w Ustawieniach.
                    </div>
                  ) : visiblePickerTemplates.length === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-text-muted">
                      W tej kategorii nie ma szablonów.
                    </div>
                  ) : (
                    visiblePickerTemplates.map((t, index) => (
                      <button
                        key={t.id}
                        disabled={disabled}
                        onClick={() => onSelect(t)}
                        className={`w-full text-left px-3 py-2 hover:bg-surface-hover/50 transition-colors ${activeIndex === index ? 'bg-accent-primary/10' : ''}`}
                      >
                        <p className="text-sm text-text-primary truncate">{t.name}</p>
                        <div className="mt-0.5 flex items-center gap-1.5">
                          {t.store && (
                            <span className="text-[10px] text-text-muted">{t.store.name}</span>
                          )}
                          {!t.storeId && (
                            <span className="text-[10px] text-accent-primary">Globalny</span>
                          )}
                          {t.category && (
                            <span className="rounded-full bg-accent-primary/10 px-1.5 py-0.5 text-[9px] text-accent-primary">
                              {t.category.name}
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
    </>
  );
}
